/**
 * ChatInsightsCard · v2.7.0 batch CX
 * ====================================
 * Surfaces the top 5 most-asked visitor questions from the chatbot inbox
 * onto the Admin Overview. Auto-aggregated from db.chatbot_conversations
 * via GET /api/admin/chatbot/insights. Refreshes every 60 seconds.
 *
 * Click any top question → routes to the Admin Chat tab so the dispatcher
 * can either (a) write a matching FAQ entry to absorb the intent locally
 * or (b) skim the latest verbatim sample in the inbox.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageCircle, TrendingUp, ChevronRight, RefreshCw, Bot, Users, Sparkles, ArrowUpRight } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("rtg_admin_token") || ""}` });

const SOURCE_LABEL = {
  faq: { label: "FAQ", color: "text-emerald-400 border-emerald-700/40 bg-emerald-900/20" },
  ai: { label: "AI", color: "text-violet-300 border-violet-700/40 bg-violet-900/20" },
  fallback: { label: "Fallback", color: "text-amber-300 border-amber-700/40 bg-amber-900/20" },
  handoff: { label: "Handoff", color: "text-orange-300 border-orange-700/40 bg-orange-900/20" },
  admin: { label: "Live", color: "text-yellow-300 border-yellow-700/40 bg-yellow-900/20" },
};

export default function ChatInsightsCard({ onJumpToTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(
        `${BASE}/api/admin/chatbot/insights?days=30&top=5`,
        { headers: headers(), timeout: 10000 },
      );
      setData(d);
      setErr(null);
    } catch (e) {
      // Graceful — if the chatbot router isn't installed yet the Overview
      // tab MUST keep working. We just hide the card content.
      setErr(e?.response?.status === 404 ? "off" : "error");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (err === "off") return null; // chatbot disabled — silently hide card

  const topQs = data?.top_questions || [];
  const totalSessions = data?.total_sessions ?? 0;
  const totalMessages = data?.total_messages ?? 0;
  const handoffRate = data?.handoff_rate ?? 0;
  const breakdown = data?.source_breakdown || {};
  const totalBot = Object.values(breakdown).reduce((a, b) => a + b, 0) || 1;

  return (
    <Card
      className="bg-gradient-to-br from-amber-950/30 via-zinc-900 to-zinc-900 border-amber-400/30 p-4"
      data-testid="chat-insights-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center">
            <TrendingUp className="h-4 w-4 text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-amber-200 flex items-center gap-2">
              Chat Insights
              <Badge variant="outline" className="text-[9px] border-amber-700/40 text-amber-400 uppercase">Last 30 days</Badge>
            </h3>
            <div className="text-[11px] text-zinc-400 mt-0.5">Top customer intents auto-aggregated from visitor chats</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            data-testid="chat-insights-refresh"
            className="h-7 w-7 p-0"
            aria-label="Refresh insights"
          ><RefreshCw className={`h-3.5 w-3.5 text-zinc-400 ${loading ? "animate-spin" : ""}`}/></Button>
          {onJumpToTab && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onJumpToTab("chat")}
              data-testid="chat-insights-jump"
              className="h-7 text-xs gap-1 text-amber-300 hover:text-amber-200"
            >Open Chat <ArrowUpRight className="h-3 w-3"/></Button>
          )}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat icon={<Users className="h-3 w-3"/>} label="Sessions" value={totalSessions}/>
        <Stat icon={<MessageCircle className="h-3 w-3"/>} label="Visitor Msgs" value={totalMessages}/>
        <Stat icon={<Sparkles className="h-3 w-3"/>} label="Handoff Rate" value={`${Math.round(handoffRate * 100)}%`} accent={handoffRate > 0.4 ? "amber" : "default"}/>
      </div>

      {/* Top questions */}
      {topQs.length === 0 ? (
        <div className="text-center py-6 text-xs text-zinc-500 italic flex flex-col items-center gap-1">
          <Bot className="h-4 w-4 text-zinc-600"/> No visitor chats yet · widget is live on /, /services, /rates, /contact
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="chat-insights-list">
          {topQs.map((q, idx) => (
            <button
              key={q.fingerprint || `q-${idx}`}
              type="button"
              data-testid={`chat-insight-row-${idx}`}
              onClick={() => onJumpToTab && onJumpToTab("chat")}
              className="w-full text-left flex items-center gap-2 p-2 rounded-md bg-zinc-950 border border-zinc-800 hover:border-amber-400/40 transition group"
            >
              <span className="flex-shrink-0 w-6 h-6 rounded-md bg-amber-400/15 text-amber-400 flex items-center justify-center text-xs font-bold">
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate group-hover:text-amber-200 transition">{q.sample}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">
                  Asked {q.count}×
                  {q.last_seen ? ` · last ${timeAgo(q.last_seen)}` : ""}
                </div>
              </div>
              <Badge variant="outline" className="bg-amber-400/10 border-amber-400/40 text-amber-300 text-[10px] font-bold">{q.count}</Badge>
              <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:text-amber-400 transition"/>
            </button>
          ))}
        </div>
      )}

      {/* Source-mix bar */}
      {totalBot > 1 && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">Response Mix</div>
          <div className="flex items-center gap-0.5 rounded-md overflow-hidden h-2">
            {Object.entries(breakdown).map(([src, n]) => {
              if (!n) return null;
              const pct = (n / totalBot) * 100;
              const colorMap = {
                faq: "bg-emerald-500",
                ai: "bg-violet-500",
                fallback: "bg-amber-500",
                handoff: "bg-orange-500",
                admin: "bg-yellow-500",
              };
              return (
                <div
                  key={src}
                  className={colorMap[src] || "bg-zinc-700"}
                  style={{ width: `${pct}%` }}
                  title={`${SOURCE_LABEL[src]?.label || src}: ${n} (${pct.toFixed(0)}%)`}
                />
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
            {Object.entries(breakdown).map(([src, n]) => {
              if (!n) return null;
              const meta = SOURCE_LABEL[src] || { label: src, color: "text-zinc-400" };
              return <span key={src} className={`${meta.color} px-1.5 py-0.5 rounded border`}>{meta.label}: {n}</span>;
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className={`rounded-md border p-2 ${accent === "amber" ? "bg-amber-900/20 border-amber-700/40" : "bg-zinc-950 border-zinc-800"}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className={`text-base font-bold mt-0.5 ${accent === "amber" ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}
